import { jsonApiVuex } from '@common/store/json-api-vuex';
import { jsonApi } from '@models/jsonApi';
import { axiosApi } from '@models/axios';
import { isEqual, flatten, uniqBy, every } from 'lodash';

let reloadTimeout; // if a reload has been requested, it will use this timeout to "wait other calls"

const makeDefaultState = () => {
  return {
    isLoadingValidationStats: false,
    isLoadingEvents: false,
    error: null,
    stats: null,
    range: null,
    environment: null,
    sources: [],
    eventNames: [],
    globalValidationSerie: [],
  };
};

const bulkifiers = {
  exactNames: null,
  patterns: null,
};
const MAX_EVENTS_PER_REQUEST = 30;

const delay = duration => new Promise(resolve => setTimeout(resolve, duration));

/**
 * @typedef BulkFindParams
 * @property {string} rangeType - either 'relative' or 'absolute'
 * @property {number|string} from - range start (number of seconds if 'relative', Date if 'absolute')
 * @property {number|string} to - range end (number of seconds if 'relative', Date if 'absolute')
 * @property {string} sourceKey - get weather for a specific source providing the integration_key
 *
 * @callback BulkifyCallback
 * @param {string[]} eventNames
 * @param {BulkFindParams} params
 * @returns {Promise<object>}
 */
class BulkifyByParameter {
  /**
   * @param {BulkifyCallback} callback
   */
  constructor(callback) {
    this.queues = [];
    this.callback = callback;
  }

  /**
   *
   * @param {string} eventName
   * @param {BulkFindParams} params
   * @returns
   */
  bulkSend(eventName, params = {}) {
    let queue = this.findQueue(params);
    if (!queue) {
      queue = this.createQueue(params);
    }
    if (!queue.events.includes(eventName)) {
      queue.events.push(eventName);
    }

    if (queue.events.length > MAX_EVENTS_PER_REQUEST) {
      this.removeQueue(queue); // todo: evol to force send request at this moment
    }

    return queue.promise;
  }

  createQueue(params) {
    const queue = {
      params,
      events: [],
      // promise,
    };

    const callDelayed = async () => {
      await delay(50);

      this.removeQueue(queue);

      return this.callback(queue.events, queue.params);
    };

    queue.promise = callDelayed();
    this.queues.push(queue);

    return queue;
  }

  findQueue(params) {
    for (const queue of this.queues) {
      if (isEqual(queue.params, params)) {
        return queue;
      }
    }
  }

  removeQueue(queue) {
    this.queues = this.queues.filter(q => q !== queue);
  }
}

export const monitoringCollectionEvent = jsonApiVuex(
  'monitoring/collection/event',
  {
    state: {
      ...makeDefaultState(),
    },
    getters: {
      getEventByName: state => name => {
        return state.entities.find(entity => {
          return entity.name === name;
        });
      },
      getEventsSources: state => {
        return uniqBy(flatten(state.entities.map(d => d.sources || [])), source => source.id);
      },
      getEventsDestinations: state => {
        return uniqBy(flatten(state.entities.map(d => d.destinations || [])), destination => destination.id);
      },
      getTotalEvents: state => {
        return state.entities.reduce((total, entity) => (total + entity.name !== '%' ? entity.total : 0), 0);
      },
      getEventsWithEventCount: state => {
        return state.entities.filter(entity => entity.total > 0);
      },
      /**
       * Returns true if the source filter means "no source at all"
       * Returns false if at least one source should be considered :
       *   - either the list of source ids has at least one element
       *   - or both environment AND source filters specify "All"
       * @param {Object} state
       * @returns {Boolean}
       */
      isSourceFilterNone({ environment, sources }) {
        const sourceIds = (sources || []).map(s => s.id);
        const allSources = sourceIds.length === 0 || sourceIds.includes(0) || sources[0].name === 'All';
        const allEnvironments = !environment || environment.id === 0;
        if (allEnvironments && allSources) {
          return false;
        } else {
          // allSources cannot be used if environment filter has a specific value
          // so we exclude it here from the list of the sources
          return (sources || []).filter(s => s.id > 0).length === 0;
        }
      },
      isLoading({ isLoadingEvents, isLoadingValidationStats }) {
        return isLoadingEvents || isLoadingValidationStats;
      },
    },
    actions: {
      /**
       * Action to fetch source Weather for 1 event name
       *
       *   Buffering : HTTP requests are mutualized if called within 50ms
       *
       * @param {object} vuexActionParams
       * @param {string} eventName
       * @returns Promise<WeatherObject>
       */
      async bulkFindEventSourceWeather(
        { commit },
        { eventName, rangeType = 'relative', from = 3600, to = 0, sourceRefs = '' }
      ) {
        const bulkifierName = eventName?.includes('%') ? 'patterns' : 'exactNames';
        if (!bulkifiers[bulkifierName]) {
          bulkifiers[bulkifierName] = new BulkifyByParameter((eventNames, params) => {
            return jsonApi.findAll('monitoring/collection/events', {
              filter: {
                ...params,
                names: eventNames.join(','),
              },
              include: 'stats.normalized, stats.weather',
            });
          });
        }

        const response = await bulkifiers[bulkifierName].bulkSend(eventName, {
          rangeType,
          from,
          to,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          sourceRefs,
        });

        for (const event of response.data) {
          if (eventName?.includes('%')) {
            commit('_addEntity', event);
          } else {
            commit('_deepSetAttributeById', { id: event.id, path: 'stats', value: event.stats || null });
          }
        }

        return response.data.find(eventInfo => eventInfo.name === eventName)?.stats?.weather;
      },
      async fetchMain({ commit, dispatch }) {
        try {
          const mainParams = {
            statuses: ['collected', 'bot'],
          };
          const res = await dispatch('fetch', mainParams);
          commit('setReport', res?.data.data.attributes);
        } catch (error) {
          commit('setError', error);
        }
      },
      async fetch({ state }, params = {}) {
        if (!state.range) return;
        const url = `/monitoring/sources-data-quality`;
        return axiosApi.get(url, {
          params: {
            ...state.range,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            sources: state.sources.map(source => source.integrationKey),
            ...params,
          },
        });
      },
      /**
       * retourne la liste des events triés, et mise en cache au besoin
       */
      async fetchEvents({ state, commit, dispatch }) {
        if (!state.range) return;
        commit('setLoadingEvents', true);
        await dispatch('findAll', {
          ...state.range,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          filter: {
            sourceRefs: state.sources.map(source => source.integrationKey),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            from: state.range.from,
            to: state.range.to,
            rangeType: state.range.rangeType,
          },
          include: 'stats',
        });
        commit('setLoadingEvents', false);
      },
      async fetchGlobalValidationStats({ commit, state }, params = {}) {
        if (!state.range) return;
        commit('setLoadingValidationStats', true);

        const url = `/monitoring/events-status`;
        const response = await axiosApi.get(url, {
          params: {
            ...state.range,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            sources: state.sources.map(source => source.integrationKey),
            ...params,
          },
        });
        const globalValidationSerie = response.data?.data.attributes;
        commit('setGlobalValidationSerie', globalValidationSerie);
        commit('setLoadingValidationStats', false);

        return globalValidationSerie;
      },
      setRange({ commit, dispatch, state }, range) {
        if (every(['rangeType', 'from', 'to'], prop => state.range && range[prop] === state.range[prop])) return;
        commit('setRange', range);
        dispatch('reload');
      },
      setSources({ commit, dispatch }, sources) {
        commit('setAllSources', sources);
        dispatch('reload');
      },
      setEnvironment({ commit, dispatch }, environment) {
        commit('setEnvironment', environment);
        dispatch('reload');
      },
      includeSource({ commit, dispatch, state }, sourceId) {
        if (state.sources.includes(sourceId)) return;

        commit('setAllSources', [...state.sources, sourceId]);
        dispatch('reload');
      },
      excludeSource({ commit, dispatch }, sourceId) {
        if (!state.sources.includes(sourceId)) return;

        commit(
          'setAllSources',
          state.sources.filter(id => id !== sourceId)
        );
        dispatch('reload');
      },
      reload({ dispatch }) {
        clearTimeout(reloadTimeout);
        reloadTimeout = setTimeout(() => {
          dispatch('fetchEvents');
          dispatch('fetchGlobalValidationStats');
        }, 30);
      },
    },

    mutations: {
      setLoadingValidationStats(state, isLoading) {
        state.isLoadingValidationStats = isLoading;
      },
      setLoadingEvents(state, isLoading) {
        state.isLoadingEvents = isLoading;
      },
      setGlobalValidationSerie(state, serie) {
        state.globalValidationSerie = serie;
      },
      setAllSources(state, sources) {
        if (JSON.stringify(sources) === JSON.stringify(state.sources)) return;
        state.sources = sources;
      },
      setRange(state, range) {
        if (JSON.stringify(range) === JSON.stringify(state.range)) return;
        state.range = range;
      },
      setReport(state, stats) {
        state.stats = stats;
        state.error = null;
      },
      setError(state, error) {
        state.error = error;
      },
      setEnvironment(state, environment) {
        state.environment = environment;
      },
      _reset(state) {
        Object.assign(state, makeDefaultState());
      },
    },
  },
  {
    defaultFindAllParams: {
      rangeType: 'relative',
      from: 86400 * 30,
    },
  }
);
